import { ReactNode } from "react";
import { Check, TriangleAlert, X } from "lucide-react";
import { twMerge } from "tailwind-merge";

interface Props {
  variant: "success" | "error" | "warning";
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  iconSize?: "md" | "lg";
}

const variantStyles = {
  success: {
    outer: "bg-[#D4F4EB]",
    inner: "bg-[#3DD68C]",
  },
  error: {
    outer: "bg-[#FFD5CC]",
    inner: "bg-brand-red",
  },
  warning: {
    outer: "bg-[#FFF0CC]",
    inner: "bg-[#F5A623]",
  },
};

const iconSizeStyles = {
  md: {
    outer: "mb-2 size-16",
    inner: "size-12",
    icon: "size-5",
  },
  lg: {
    outer: "mb-2.5 h-[118px] w-[116px]",
    inner: "size-[100px]",
    icon: "size-14",
  },
};

const StatusGroup = ({
  variant,
  title,
  description,
  children,
  iconSize = "md",
}: Props) => {
  const styles = variantStyles[variant];
  const sizes = iconSizeStyles[iconSize];

  return (
    <div className="flex flex-col items-center">
      <div
        className={twMerge(
          "animate-icon-pop flex items-center justify-center rounded-full",
          sizes.outer,
          styles.outer,
        )}
      >
        <div
          className={twMerge(
            "flex items-center justify-center rounded-full",
            sizes.inner,
            styles.inner,
          )}
        >
          {variant === "success" ? (
            <Check
              className={twMerge("animate-icon-draw text-white", sizes.icon)}
              strokeWidth={3}
            />
          ) : variant === "warning" ? (
            <TriangleAlert
              className={twMerge("text-white", sizes.icon)}
              strokeWidth={2.5}
            />
          ) : (
            <X className={twMerge("text-white", sizes.icon)} strokeWidth={3} />
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="text-xl font-semibold text-black">{title}</h2>
        {description && (
          <p className="text-content-secondary text-sm">{description}</p>
        )}
      </div>

      {children && <div className="mt-6 w-full">{children}</div>}
    </div>
  );
};

export default StatusGroup;
